/****
 * Mobile widget page that provides a dedicated full-screen experience
 * for viewing dashboard widgets on mobile devices.
 *
 * Dynamically loads and renders the selected widget based on the
 * route parameter, supports special dashboard features such as
 * World Clocks, Account Settings, and Accessibility Settings,
 * and provides mobile-friendly navigation back to the dashboard.
 */
import { useMsal } from "@azure/msal-react";
import { ChevronLeft } from "@untitledui/icons";
import { Suspense } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";

import { useAuth } from "@/hooks/useAuth";
import { ROLE_NAME_MAP } from "@/shared/utils/maps";
import { selectFeatures, selectSelectedRole } from "@/store/userSlice";
import { widgetComponentMap } from "@/types/index";
import TimezoneBar from "@/Components/TimezoneBar";
import AccessibilityPanel from "@/features/AccessibilityPanel/AccessibilityPanel";
import ProfileModal from "@/features/Navbar/components/ProfileModal";


const MobileWidgetPage = () => {
  const navigate = useNavigate();
  const { account } = useAuth();
  const { widgetKey } = useParams();
  const { accounts } = useMsal();
  const { t } = useTranslation();
  const selectedRole = useSelector(selectSelectedRole);
  const features = useSelector(selectFeatures);
  const feature = features.find((f) =>
  f.route_path?.split("/").pop() === widgetKey
);
let Component = null;

if (widgetKey === "world-clocks") {
  Component = TimezoneBar;
} else if (widgetKey === "account-settings") {
  Component = ProfileModal;
} else if (widgetKey === "accessibility") {
  Component = AccessibilityPanel;
} else if (feature?.widget_component) {
  Component =
    widgetComponentMap[
      feature.widget_component as keyof typeof widgetComponentMap
    ];
}
  const upn =
    (accounts[0]?.idTokenClaims as any)?.preferred_username || accounts[0]?.username || "";

  const displayName = accounts[0]?.name || account?.displayName || "Guest";

  return (
    <div className="min-h-[100dvh] bg-[var(--bg-main)] text-[var(--text-color)]">
      <div className="flex items-center gap-3 px-4 py-3 bg-[var(--panel-bg)] border-b border-[var(--border-color)]">
        <button onClick={() => navigate(-1)}>
          <ChevronLeft className="w-5 h-5" />
        </button>

        <div>
          <h1 className="text-lg font-semibold">
  {feature?.title_key ? t(feature.title_key) : ""}
</h1>
         <p className="text-xs text-[var(--text-secondary)]">
  {feature?.subtitle_key ? t(feature.subtitle_key) : ""}
</p>
        </div>
      </div>

      <div className="pb-6 flex flex-col min-h-0">
        <Suspense fallback={<div className="p-4 text-[var(--text-secondary)]">Loading...</div>}>
          {Component ? (
            <Component
              open={true}
              upn={upn}
             profile={{
  profileDisplayName: displayName,
  profileEmail: upn,
  organization: "Mastercard Foundation",
  role: ROLE_NAME_MAP[selectedRole] || selectedRole,
}}
              onClose={() => navigate(-1)}
            />
          ) : (
            <div className="p-4 text-[var(--text-color)]">Coming soon</div>
          )}
        </Suspense>
      </div>
    </div>
  );
};

export default MobileWidgetPage;
